import type { Socket as NodeSocketType } from 'net'

import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { v4 as uuid } from 'uuid'

import { Socket as CapacitorSocketPlugin } from '@/integrations/SocketPlugin'
import { isElectron } from '@/utils/device-type'

interface SocketDriver {
  closed: boolean
  open(address: string, port: number, callback: () => void, reject: (error: Error) => void): void
  write(
    data: string | Uint8Array<ArrayBufferLike>,
    callback: () => void,
    reject: (error: Error) => void
  ): void
  shutdownWrite(callback: () => void, reject: (error: Error) => void): void
  close(): void
  onData(callback: (data: Buffer) => void): void
}

class NodeSocket implements SocketDriver {
  private socket: NodeSocketType
  public closed: boolean

  constructor() {
    const net = window.require('net')
    this.closed = true
    this.socket = new net.Socket()
  }

  open(address: string, port: number, callback: () => void, reject: (error: Error) => void) {
    this.socket.on('error', reject)
    this.socket.connect(port, address, callback)
    this.closed = false
  }

  write(data: string, callback: () => void, reject: (error: Error) => void) {
    this.socket.write(data, 'utf8', error => {
      if (error) {
        reject(error)
      } else {
        callback()
      }
    })
  }

  onData(callback: (data: Buffer) => void) {
    this.socket.on('data', callback)
  }

  shutdownWrite(callback: () => void, reject: (error: Error) => void) {
    this.socket.on('error', reject)
    this.socket.end('', 'utf8', callback)
    this.closed = true
  }

  close() {
    this.socket.destroy()
    this.closed = true
  }
}

class CordovaSocket implements SocketDriver {
  // @ts-expect-error Type from cordova plugin
  private socket: Socket
  public closed: boolean
  constructor() {
    // @ts-expect-error Type from cordova plugin
    this.socket = new Socket()
    this.closed = true
    // add all global listener to avoid android issues
    this.socket.onError = () => {}
    this.socket.onData = () => {}
    this.socket.onClose = () => {
      this.closed = true
    }
  }

  open(address: string, port: number, callback: () => void, reject: (error: Error) => void) {
    this.socket.open(address, port, callback, reject)
    this.closed = false
  }

  write(
    data: string | Uint8Array<ArrayBufferLike>,
    callback: () => void,
    reject: (error: Error) => void
  ) {
    this.socket.write(data, callback, reject)
  }

  shutdownWrite(callback: () => void, reject: (error: Error) => void) {
    this.socket.shutdownWrite(callback, reject)
  }

  close() {
    this.socket.close()
    this.closed = true
  }

  onData(callback: (data: Buffer) => void) {
    this.socket.onData = callback
  }
}

class CapacitorSocket implements SocketDriver {
  public closed: boolean
  private socketKey: string
  private dataListener?: PluginListenerHandle
  private errorListener?: PluginListenerHandle
  private closeListener?: PluginListenerHandle
  private onDataCallback?: (data: Buffer) => void

  constructor() {
    this.closed = true
    this.socketKey = uuid()
  }

  async open(address: string, port: number, callback: () => void, reject: (error: Error) => void) {
    try {
      await CapacitorSocketPlugin.open({ socketKey: this.socketKey, address, port })
      this.closed = false

      this.errorListener = await CapacitorSocketPlugin.addListener('error', error => {
        if (error.socketKey === this.socketKey) {
          reject(new Error(error.message))
        }
      })

      this.closeListener = await CapacitorSocketPlugin.addListener('close', event => {
        if (event.socketKey === this.socketKey) {
          this.closed = true
          this.cleanup()
        }
      })

      this.dataListener = await CapacitorSocketPlugin.addListener('data', data => {
        if (data.socketKey === this.socketKey && this.onDataCallback) {
          // Convert string to Buffer to match the interface
          const buffer = Buffer.from(data.data, 'utf8')
          this.onDataCallback(buffer)
        }
      })

      callback()
    } catch (error) {
      this.cleanup()
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  }

  async write(
    data: string | Uint8Array<ArrayBufferLike>,
    callback: () => void,
    reject: (error: Error) => void
  ) {
    try {
      if (typeof data === 'string') {
        await CapacitorSocketPlugin.write({ socketKey: this.socketKey, data })
      } else {
        // Send as byte array for binary data
        const dataBytes = Array.from(data)
        await CapacitorSocketPlugin.write({ socketKey: this.socketKey, dataBytes })
      }
      callback()
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  }

  async shutdownWrite(callback: () => void, reject: (error: Error) => void) {
    try {
      await CapacitorSocketPlugin.shutdownWrite({ socketKey: this.socketKey })
      callback()
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  }

  async close() {
    try {
      await CapacitorSocketPlugin.close({ socketKey: this.socketKey })
      this.closed = true
      this.cleanup()
    } catch (_error) {
      // Ignore errors when closing
      this.closed = true
      this.cleanup()
    }
  }

  onData(callback: (data: Buffer) => void) {
    this.onDataCallback = callback
  }

  private cleanup() {
    this.dataListener?.remove()
    this.errorListener?.remove()
    this.closeListener?.remove()
  }
}

class SocketFactory {
  static getSocket(useNewDriver?: boolean): SocketDriver {
    if (isElectron) {
      return new NodeSocket()
    } else {
      if (useNewDriver && Capacitor.isPluginAvailable('Socket')) {
        return new CapacitorSocket()
      }
      return new CordovaSocket()
    }
  }
}

export default SocketFactory
